import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Compass, Hammer, Heart, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { COMPANY, STATS, VALUES } from "@/data/mock";

export default function About() {
  return (
    <div data-testid="about-page" className="bg-brand-bgOchreSoft">
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-12 md:pb-20">
        <p className="text-xs uppercase tracking-[0.22em] text-brand-terracotta font-medium">
          About us
        </p>
        <h1 className="mt-4 font-display text-5xl md:text-7xl font-semibold tracking-tighter leading-[0.95] text-brand-ink max-w-4xl text-balance">
          A small Berkshire builder doing careful work for the people next door.
        </h1>
        <p className="mt-6 text-lg text-brand-inkSoft max-w-2xl leading-relaxed">
          A new name, but not new to the trade. Over a decade on the tools across Berkshire — and
          now stepping out under my own banner to do the work the way I’ve always wanted to.
        </p>
      </section>

      {/* Image + story */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-20 md:pb-28 grid md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-6">
          <div className="relative aspect-[4/5] overflow-hidden rounded-3xl border border-brand-line">
            <img
              src="https://customer-assets.emergentagent.com/job_build-neighbours/artifacts/8k29qxxe_3c4d0133-32e5-44c0-ab88-18e62a8d2753.jpg"
              alt="Petrit Shehi — founder"
              className="w-full h-full object-cover"
              data-testid="about-portrait"
            />
            <div className="absolute bottom-5 left-5 right-5 rounded-2xl bg-brand-bg/95 backdrop-blur px-4 py-3 border border-brand-line">
              <p className="text-[11px] uppercase tracking-[0.18em] text-brand-inkSoft">
                Founder · on the tools
              </p>
              <p className="font-display font-medium text-brand-ink leading-tight">
                Petrit Shehi
              </p>
            </div>
          </div>
        </div>
        <div className="md:col-span-6">
          <p className="text-xs uppercase tracking-[0.22em] text-brand-terracotta font-medium">
            Meet the founder
          </p>
          <h2 className="mt-3 font-display text-4xl md:text-5xl font-medium tracking-tight text-brand-ink">
            Hi, I’m Petrit. New company, old-school standards.
          </h2>
          <div className="mt-6 space-y-5 text-brand-inkSoft leading-relaxed">
            <p>
              I’m <strong className="text-brand-ink font-medium">Petrit Shehi</strong>, the founder
              of Neighbours Brick &amp; Build. I’ve spent the last{" "}
              <strong className="text-brand-ink font-medium">11 years</strong> on the tools across
              Berkshire — bricklaying, garden walls, patios, repointing, extensions and full
              refurbs.
            </p>
            <p>
              I started young, apprenticed under proper old-school brickies, and worked my way up
              through bigger sites picking up the trades around me. I’ve seen jobs done well and
              I’ve seen jobs done badly — and that taught me which corners are never worth cutting.
            </p>
            <p>
              Neighbours Brick &amp; Build is the company I always wanted to run: fair fixed prices,
              clean sites, honest timelines, and a single phone number that I answer myself. You
              get me planning the job, you get me on site, and you get me at the end of the call if
              anything ever needs sorting.
            </p>
            <p>
              The company is new — but the hands aren’t. Every job leaves the site cleaner than I
              found it, on time and on the price we agreed. That’s the only way I know how to work.
            </p>
          </div>
          <div className="mt-8">
            <Button
              asChild
              data-testid="about-cta-quote"
              className="rounded-full h-12 px-7 bg-brand-terracotta hover:bg-brand-terracottaDark text-white"
            >
              <Link to="/contact">
                Talk to us <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-brand-bgSageSoft border-y border-brand-line">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <h2 className="font-display text-4xl md:text-5xl font-medium tracking-tight text-brand-ink max-w-2xl">
            How we work, in plain English.
          </h2>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {VALUES.map((v, i) => {
              const Icon = [Heart, Compass, Hammer, ShieldCheck][i];
              const palette = [
                { bg: "bg-brand-bgTerracotta", fg: "text-brand-terracotta" },
                { bg: "bg-brand-bgSage", fg: "text-brand-sageDark" },
                { bg: "bg-brand-bgOchre", fg: "text-brand-ochreDark" }
              ][i % 3];
              return (
                <div
                  key={v.title}
                  className="rounded-2xl bg-white border border-brand-line p-6 hover:-translate-y-0.5 transition-transform"
                  data-testid={`about-value-${v.title}`}
                >
                  <div className={`w-11 h-11 rounded-full inline-flex items-center justify-center ${palette.bg} ${palette.fg}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="mt-5 font-display text-xl font-medium tracking-tight text-brand-ink">
                    {v.title}
                  </h3>
                  <p className="mt-2 text-brand-inkSoft text-[15px] leading-relaxed">{v.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28 grid md:grid-cols-4 gap-10">
        {STATS.map((s) => (
          <div key={s.label}>
            <div className="font-display text-5xl md:text-6xl font-semibold tracking-tighter text-brand-ink">
              {s.value}
            </div>
            <div className="mt-2 text-brand-inkSoft">{s.label}</div>
          </div>
        ))}
      </section>
    </div>
  );
}
